import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  OnInit,
  inject,
  signal,
} from '@angular/core';
import { Router, RouterModule } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { passwordValidator } from '../validators/password.validator';
import { emailValidator } from '../validators/email.validator';
import { SocialLoginComponent } from '@app/components/shared/social-login/social-login.component';
import { InputType } from '@app/utils/enums/input-type.enum';
import { LocalStorageKeys } from '@app/utils/enums/local-storage';
import { AppRoutes } from '@app/utils/enums/routes.enum';
import { IconComponent } from '@app/components/shared/icon/icon.component';
import { StatusModalComponent } from '@app/components/shared/status-modal/status-modal.component';
import { LOGIN_OPTIONS } from '@app/utils/constants/authenticator.constants';
import { IconName } from '@app/utils/enums/icon-enum';
import { LogInDataRequest } from '@app/utils/interfaces/log-in.interface';
import { AuthService } from '@app/services/auth/auth.service';

@Component({
  selector: 'app-login-form',
  standalone: true,
  templateUrl: './login-form.component.html',
  styleUrls: ['./login-form.component.scss'],
  imports: [
    CommonModule,
    ReactiveFormsModule,
    RouterModule,
    TranslateModule,
    SocialLoginComponent,
    IconComponent,
    MatCheckboxModule,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LoginFormComponent implements OnInit {
  private readonly fb = inject(FormBuilder);
  private readonly translateService = inject(TranslateService);
  private readonly router = inject(Router);
  private readonly dialog = inject(MatDialog);
  private readonly destroyRef = inject(DestroyRef);

  private readonly authService = inject(AuthService);

  protected failedAttempts = signal(0);
  protected showAttemptsWarning = signal(false);
  protected attemptsWarningMessage = signal('');

  public loginForm!: FormGroup;
  public isPasswordVisible = false;
  public inputType = InputType;
  public iconName = IconName;
  public appRoutes: typeof AppRoutes = AppRoutes;

  ngOnInit(): void {
    this.loginForm = this.fb.group({
      email: [this.getSavedLogin(), [Validators.required, emailValidator()]],
      password: [
        '',
        [Validators.required, Validators.minLength(8), Validators.maxLength(20), passwordValidator],
      ],
      rememberMe: [this.getRememberMe()],
    });
  }

  public onSubmit(): void {
    this.loginForm.markAllAsTouched();

    if (this.loginForm.invalid) {
      return;
    }

    const { email, password, rememberMe } = this.loginForm.value;
    const formData: LogInDataRequest = { email, password };

    this.handleStorage(rememberMe, formData);

    this.authService
      .login(formData)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          const is2FAEnabled = localStorage.getItem(LocalStorageKeys.TWO_FACTOR_STATUS_KEY);
          this.router.navigate([
            is2FAEnabled === 'true' ? AppRoutes.AUTHENTICATOR : AppRoutes.MAIN,
          ]);
        },
        error: (error) => {
          const invalidPassword = this.translateService.instant(
            'loginPageComponent.errors.requestError',
          );
          const lockedAccount = this.translateService.instant(
            'loginPageComponent.errors.errorLocked',
          );
          const invalidUser = this.translateService.instant(
            'loginPageComponent.errors.notFoundError',
          );

          if (error.error.message === invalidPassword || error.error.message === invalidUser) {
            this.failedAttempts.update((attempts) => attempts + 1);
            this.handleFailedAttempts();
          } else if (error.error.message === lockedAccount) {
            this.handleLockedAccount();
          } else {
            this.handleGenericError();
          }
        },
      });
  }

  private handleFailedAttempts(): void {
    const attempts = this.failedAttempts();

    if (attempts === 1) {
      this.showAttemptsWarning.set(true);
      this.attemptsWarningMessage.set(
        this.translateService.instant('loginPageComponent.errors.firstAttempt'),
      );
    } else if (attempts === 2) {
      this.showAttemptsWarning.set(true);
      this.attemptsWarningMessage.set(
        this.translateService.instant('loginPageComponent.errors.secondAttempt'),
      );
    } else if (attempts >= 3) {
      this.handleLockedAccount();
    }

    this.loginForm.get('password')?.reset();
  }

  private handleLockedAccount(): void {
    const dialogRef = this.dialog.open(StatusModalComponent, {
      ...LOGIN_OPTIONS,
      data: {
        isSuccess: false,
        title: 'loginPageComponent.errors.accountLockedTitle',
        description: 'loginPageComponent.errors.accountLockedText',
      },
    });

    dialogRef
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.loginForm.get('password')?.reset('');
        this.showAttemptsWarning.set(false);
      });
  }

  private handleGenericError(): void {
    const dialogRef = this.dialog.open(StatusModalComponent, {
      ...LOGIN_OPTIONS,
      data: {
        isSuccess: false,
        title: 'loginPageComponent.errors.errorTitle',
        description: 'loginPageComponent.errors.errorText',
      },
    });

    dialogRef
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.loginForm.get('password')?.reset();
        this.showAttemptsWarning.set(false);
      });
  }

  private getSavedLogin(): string {
    const saved = localStorage.getItem(LocalStorageKeys.SAVED_LOGIN);
    return saved ?? '';
  }

  private getRememberMe(): boolean {
    const value = localStorage.getItem(LocalStorageKeys.REMEMBER_CHECKBOX);
    return value ? JSON.parse(value) : false;
  }

  private handleStorage(rememberMe: boolean, credentials: LogInDataRequest): void {
    if (rememberMe) {
      localStorage.setItem(LocalStorageKeys.SAVED_LOGIN, credentials.email);
      localStorage.setItem(LocalStorageKeys.REMEMBER_CHECKBOX, JSON.stringify(rememberMe));
      return;
    }

    localStorage.removeItem(LocalStorageKeys.SAVED_LOGIN);
    localStorage.removeItem(LocalStorageKeys.REMEMBER_CHECKBOX);
  }

  public togglePasswordVisibility(): void {
    this.isPasswordVisible = !this.isPasswordVisible;
  }
}
